import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { MenuProps } from "antd";
import { MoreOutlined } from "@ant-design/icons";
import styled, { css } from "styled-components";
import { AppDropdown } from "../Dropdown/AppDropdown";
import { PHONE_MAX_WIDTH, SAFE_AREA, TAP_TARGET, TAP_TARGET_PRIMARY, mediaPhone } from "@/shared/theme/breakpoints";
import { pressedFade, pressedTint } from "@/shared/theme/pressed";
import { useMaxWidth } from "@/shared/hooks/useMaxWidth";
import { phoneSheetDialog } from "../Modal/phoneSheet";

export interface ActionBarSubAction {
	key: string;
	label: string;
	/** Row label once the parent has collapsed under "More", e.g. "Export as CSV". */
	overflowLabel?: string;
	onSelect: () => void;
}

export interface ActionBarAction {
	key: string;
	label: string;
	/** Phone label; the full label is kept for the menu and assistive tech. */
	shortLabel?: string;
	icon?: React.ReactNode;
	/** Primary actions render inline while they fit; overflow actions always live under "More". */
	priority: "primary" | "overflow";
	danger?: boolean;
	disabled?: boolean;
	/** Receives the originating click, so callers can honour modifier-click (open in new tab). */
	onSelect?: (event: React.MouseEvent<HTMLElement>) => void;
	/** Inline, the item opens a dropdown of these; under "More" they become rows of their own. */
	menu?: ActionBarSubAction[];
	/** Presentation tweaks for inline and phone sheet rows (e.g. a transient success color). */
	style?: React.CSSProperties;
	/**
	 * Renders a custom control (e.g. a stateful copy button) instead of the default button. These
	 * always stay inline, so they never need an equivalent menu row.
	 */
	render?: () => React.ReactNode;
}

export interface ActionBarProps {
	actions: ActionBarAction[];
	/** Which edge the inline actions sit against. */
	align?: "start" | "end";
	/** Space between inline items; defaults to 8px. A consumer may override `gap` in CSS per breakpoint. */
	gap?: number;
	/** Accessible name of the "More" button and title of the phone sheet. */
	moreLabel?: string;
	className?: string;
	"aria-label"?: string;
}

const DEFAULT_GAP = 8;

/* ─── Inline row ─── */

/*
 * Sizing defaults sit in :where(), which has zero specificity, so a consumer's styled(ActionBar)
 * always overrides them. Without it, consumer and default tie on specificity and whichever was
 * injected last wins, which flips after a hot reload or a different chunk load order.
 */
const Bar = styled.div<{ $align: "start" | "end"; $gap: number }>`
	position: relative;
	display: flex;
	align-items: center;

	:where(&) {
		justify-content: ${({ $align }) => ($align === "end" ? "flex-end" : "flex-start")};
		gap: ${({ $gap }) => $gap}px;
		/* Asks for its full natural width but may shrink; the overflow logic then measures what it got. */
		flex: 0 1 var(--action-bar-width, auto);
		min-width: ${TAP_TARGET}px;
		max-width: 100%;
	}
`;

/** Off-screen copy of every primary button, so widths are known even while one sits under "More". */
const MeasureRow = styled.div<{ $gap: number }>`
	position: absolute;
	top: 0;
	left: 0;
	display: flex;
	gap: ${({ $gap }) => $gap}px;
	visibility: hidden;
	pointer-events: none;
	white-space: nowrap;
	height: 0;
	overflow: hidden;
`;

const itemBase = css`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	flex-shrink: 0;
	padding: 6px 10px;
	background: transparent;
	border: none;
	border-radius: 8px;
	color: #4460ef;
	font-family: inherit;
	font-size: 16px;
	font-weight: 500;
	line-height: 24px;
	white-space: nowrap;
	cursor: pointer;
	transition: opacity 0.2s;

	&:hover:not(:disabled) {
		opacity: 0.7;
	}

	&:disabled {
		color: #9aa3c7;
		cursor: not-allowed;
	}

	&:focus-visible {
		outline: 2px solid #3a0ca3;
		outline-offset: 2px;
	}
`;

const Item = styled.button<{ $danger?: boolean }>`
	${itemBase}
	${({ $danger }) => $danger && "color: #d30024;"}

	${mediaPhone`
		min-height: ${TAP_TARGET}px;
		font-size: 14px;
		padding: 6px 8px;
	`}
	${pressedFade}
`;

const FullLabel = styled.span<{ $hasShort: boolean }>`
	${({ $hasShort }) => $hasShort && mediaPhone`display: none;`}
`;

const ShortLabel = styled.span`
	display: none;
	${mediaPhone`display: inline;`}
`;

const MoreButton = styled.button`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	width: ${TAP_TARGET}px;
	height: ${TAP_TARGET}px;
	padding: 0;
	border: none;
	border-radius: 50%;
	background: transparent;
	color: #3a0ca3;
	font-size: 20px;
	cursor: pointer;

	@media (hover: hover) {
		&:hover {
			background: rgba(58, 12, 163, 0.06);
		}
	}

	&:focus-visible {
		outline: 2px solid #3a0ca3;
		outline-offset: 2px;
	}

	${pressedTint}
`;

/* ─── Phone action sheet ─── */

const SheetOverlay = styled.div`
	position: fixed;
	inset: 0;
	z-index: 2200;
	display: flex;
	align-items: flex-end;
	background: rgba(0, 0, 0, 0.45);
	overscroll-behavior: contain;
`;

const SheetPanel = styled.div`
	${phoneSheetDialog}
	background: #fff;
	padding: 8px 0 calc(16px + ${SAFE_AREA.bottom});
	box-shadow: 0 -8px 24px rgba(21, 89, 165, 0.16);

	&:focus {
		outline: none;
	}
`;

const SheetHandle = styled.div`
	width: 40px;
	height: 4px;
	margin: 0 auto 8px;
	border-radius: 2px;
	background: #d6dcf5;
`;

const SheetTitle = styled.h2`
	margin: 0;
	padding: 4px 20px 12px;
	font-family: "Poppins", sans-serif;
	font-size: 16px;
	font-weight: 600;
	color: #1a1a1a;
	border-bottom: 1px solid #e6e9f8;
`;

const SheetList = styled.div`
	display: flex;
	flex-direction: column;
	padding: 8px 0;
	overflow-y: auto;
`;

const SheetIcon = styled.span`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex: 0 0 24px;
	width: 24px;
	height: 24px;
	color: inherit;
`;

const SheetRow = styled.button<{ $danger?: boolean }>`
	display: flex;
	align-items: center;
	gap: 12px;
	width: 100%;
	min-height: ${TAP_TARGET_PRIMARY}px;
	padding: 0 20px;
	border: none;
	background: transparent;
	color: ${({ $danger, disabled }) => ($danger ? "#d30024" : disabled ? "#9aa3c7" : "#4460ef")};
	font-family: inherit;
	font-size: 16px;
	font-weight: 500;
	text-align: left;
	cursor: pointer;

	&:disabled {
		color: #9aa3c7;
		cursor: not-allowed;
	}

	&:focus-visible {
		outline: 2px solid #3a0ca3;
		outline-offset: -2px;
	}

	${pressedTint}
`;

const SheetCancel = styled.button`
	display: block;
	width: calc(100% - 40px);
	min-height: ${TAP_TARGET_PRIMARY}px;
	margin: 8px 20px 0;
	border: 1px solid #3a0ca3;
	border-radius: 999px;
	background: #fff;
	color: #3a0ca3;
	font-family: inherit;
	font-size: 16px;
	font-weight: 600;
	cursor: pointer;

	&:focus-visible {
		outline: 2px solid #3a0ca3;
		outline-offset: 2px;
	}

	${pressedTint}
`;

interface ActionSheetProps {
	title: string;
	actions: ActionBarAction[];
	onClose: () => void;
	onSelect: (action: ActionBarAction, event: React.MouseEvent<HTMLElement>) => void;
}

const FOCUSABLE = "button:not(:disabled)";

const ActionSheet: React.FC<ActionSheetProps> = ({ title, actions, onClose, onSelect }) => {
	const panelRef = useRef<HTMLDivElement>(null);
	const titleId = `action-sheet-${React.useId()}`;

	useEffect(() => {
		const panel = panelRef.current;
		panel?.querySelector<HTMLElement>(FOCUSABLE)?.focus();

		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				event.preventDefault();
				onClose();
				return;
			}
			if (event.key !== "Tab" || !panel) return;
			const focusable = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));
			if (focusable.length === 0) return;
			const first = focusable[0];
			const last = focusable[focusable.length - 1];
			if (event.shiftKey && document.activeElement === first) {
				event.preventDefault();
				last.focus();
			} else if (!event.shiftKey && document.activeElement === last) {
				event.preventDefault();
				first.focus();
			}
		};
		document.addEventListener("keydown", onKeyDown);
		return () => document.removeEventListener("keydown", onKeyDown);
	}, [onClose]);

	return createPortal(
		<SheetOverlay onClick={onClose}>
			<SheetPanel
				ref={panelRef}
				role="dialog"
				aria-modal="true"
				aria-labelledby={titleId}
				tabIndex={-1}
				onClick={(event) => event.stopPropagation()}
			>
				<SheetHandle aria-hidden />
				<SheetTitle id={titleId}>{title}</SheetTitle>
				<SheetList role="menu" aria-labelledby={titleId}>
					{actions.map((action) => (
						<SheetRow
							key={action.key}
							type="button"
							role="menuitem"
							$danger={action.danger}
							disabled={action.disabled}
							style={action.style}
							onClick={(event) => onSelect(action, event)}
						>
							{action.icon && <SheetIcon aria-hidden="true">{action.icon}</SheetIcon>}
							{action.label}
						</SheetRow>
					))}
				</SheetList>
				<SheetCancel type="button" onClick={onClose}>
					Cancel
				</SheetCancel>
			</SheetPanel>
		</SheetOverlay>,
		document.body,
	);
};

/* ─── ActionBar ─── */

const ItemLabel: React.FC<{ action: ActionBarAction }> = ({ action }) => (
	<>
		{action.icon}
		<FullLabel $hasShort={Boolean(action.shortLabel)}>{action.label}</FullLabel>
		{action.shortLabel && <ShortLabel aria-hidden>{action.shortLabel}</ShortLabel>}
	</>
);

/**
 * A row of page or record actions that never overflows its container. Primary actions sit inline
 * while they fit; the rest collapse into a "More" button that opens a dropdown on desktop and a
 * bottom action sheet on phones. Widths are measured, so it adapts at every container size.
 */
export const ActionBar: React.FC<ActionBarProps> = ({
	actions,
	align = "end",
	gap: gapProp = DEFAULT_GAP,
	moreLabel = "More actions",
	className,
	"aria-label": ariaLabel,
}) => {
	const isPhone = useMaxWidth(PHONE_MAX_WIDTH);
	const barRef = useRef<HTMLDivElement>(null);
	const measureRef = useRef<HTMLDivElement>(null);
	const customRefs = useRef(new Map<string, HTMLElement>());
	const [visibleCount, setVisibleCount] = useState(Number.POSITIVE_INFINITY);
	const [naturalWidth, setNaturalWidth] = useState<number>();
	const [sheetOpen, setSheetOpen] = useState(false);
	const moreRef = useRef<HTMLButtonElement>(null);

	const custom = useMemo(() => actions.filter((a) => a.render), [actions]);
	const collapsible = useMemo(() => actions.filter((a) => !a.render && a.priority === "primary"), [actions]);
	const alwaysOverflow = useMemo(() => actions.filter((a) => !a.render && a.priority === "overflow"), [actions]);

	const fitCount = useCallback(() => {
		const bar = barRef.current;
		const measure = measureRef.current;
		if (!bar || !measure) return;

		// The rendered gap, so consumers can change it per breakpoint in CSS.
		const cssGap = parseFloat(getComputedStyle(bar).columnGap);
		const gap = Number.isFinite(cssGap) ? cssGap : gapProp;

		const customWidth = custom.reduce((sum, a) => sum + (customRefs.current.get(a.key)?.offsetWidth ?? 0) + gap, 0);
		const widths = Array.from(measure.children, (child) => (child as HTMLElement).offsetWidth);
		const moreWidth = TAP_TARGET + gap;
		const allWidth = widths.reduce((sum, w) => sum + w + gap, 0);
		setNaturalWidth(customWidth + allWidth + (alwaysOverflow.length > 0 ? moreWidth : 0) - gap);

		const available = bar.clientWidth;
		// Not laid out yet (hidden tab, jsdom): show everything rather than collapsing it all.
		if (available === 0) {
			setVisibleCount(Number.POSITIVE_INFINITY);
			return;
		}
		const fits = (count: number, needsMore: boolean) => {
			const used = widths.slice(0, count).reduce((sum, w) => sum + w + gap, 0);
			return customWidth + used + (needsMore ? moreWidth : 0) - gap <= available;
		};

		let count = widths.length;
		while (count > 0 && !fits(count, alwaysOverflow.length > 0 || count < widths.length)) count -= 1;
		setVisibleCount(count);
	}, [custom, alwaysOverflow.length, gapProp]);

	useLayoutEffect(() => {
		fitCount();
		const bar = barRef.current;
		if (!bar || typeof ResizeObserver === "undefined") return undefined;
		const observer = new ResizeObserver(() => fitCount());
		observer.observe(bar);
		if (measureRef.current) observer.observe(measureRef.current);
		return () => observer.disconnect();
	}, [fitCount]);

	const inline = collapsible.slice(0, visibleCount);
	// A collapsed action with a submenu becomes one row per entry.
	const overflow = [...collapsible.slice(visibleCount), ...alwaysOverflow].flatMap<ActionBarAction>((action) =>
		action.menu
			? action.menu.map((sub) => ({
					key: `${action.key}:${sub.key}`,
					label: sub.overflowLabel ?? `${action.label} ${sub.label}`,
					icon: action.icon,
					priority: "overflow",
					disabled: action.disabled,
					onSelect: sub.onSelect,
				}))
			: [action],
	);

	const select = useCallback((action: ActionBarAction, event: React.MouseEvent<HTMLElement>) => {
		if (action.disabled) return;
		setSheetOpen(false);
		action.onSelect?.(event);
	}, []);

	const closeSheet = useCallback(() => {
		setSheetOpen(false);
		moreRef.current?.focus();
	}, []);

	const menu: MenuProps = {
		items: overflow.map((action) => ({
			key: action.key,
			label: action.label,
			icon: action.icon,
			danger: action.danger,
			disabled: action.disabled,
		})),
		onClick: ({ key, domEvent }) => {
			const action = overflow.find((a) => a.key === key);
			if (action) select(action, domEvent as React.MouseEvent<HTMLElement>);
		},
	};

	const moreButton = (
		<MoreButton
			ref={moreRef}
			type="button"
			aria-label={moreLabel}
			aria-haspopup={isPhone ? "dialog" : "menu"}
			aria-expanded={isPhone ? sheetOpen : undefined}
			onClick={isPhone ? () => setSheetOpen(true) : undefined}
		>
			<MoreOutlined />
		</MoreButton>
	);

	return (
		<Bar
			ref={barRef}
			className={className}
			$align={align}
			$gap={gapProp}
			role="toolbar"
			aria-label={ariaLabel}
			style={
				naturalWidth
					? ({ "--action-bar-width": `${Math.ceil(naturalWidth)}px` } as React.CSSProperties)
					: undefined
			}
		>
			<MeasureRow ref={measureRef} $gap={gapProp} aria-hidden>
				{collapsible.map((action) => (
					<Item
						key={action.key}
						as="span"
						data-action-bar-item=""
						$danger={action.danger}
						style={action.style}
					>
						<ItemLabel action={action} />
					</Item>
				))}
			</MeasureRow>

			{actions.map((action) => {
				if (action.render) {
					return (
						<span
							key={action.key}
							style={{ display: "inline-flex", flexShrink: 0 }}
							ref={(el) => {
								if (el) customRefs.current.set(action.key, el);
								else customRefs.current.delete(action.key);
							}}
						>
							{action.render()}
						</span>
					);
				}
				if (!inline.includes(action)) return null;
				const item = (
					<Item
						key={action.key}
						type="button"
						data-action-bar-item=""
						$danger={action.danger}
						disabled={action.disabled}
						style={action.style}
						aria-label={action.shortLabel ? action.label : undefined}
						aria-haspopup={action.menu ? "menu" : undefined}
						onClick={action.menu ? undefined : (event) => select(action, event)}
					>
						<ItemLabel action={action} />
					</Item>
				);
				if (!action.menu) return item;
				return (
					<AppDropdown
						key={action.key}
						disabled={action.disabled}
						trigger={isPhone ? ["click"] : ["hover", "click"]}
						placement="bottomRight"
						menu={{
							items: action.menu.map((sub) => ({ key: sub.key, label: sub.label })),
							onClick: ({ key }) => action.menu?.find((sub) => sub.key === key)?.onSelect(),
						}}
					>
						{item}
					</AppDropdown>
				);
			})}

			{overflow.length > 0 &&
				(isPhone ? (
					moreButton
				) : (
					<AppDropdown menu={menu} trigger={["click"]} placement="bottomRight">
						{moreButton}
					</AppDropdown>
				))}

			{isPhone && sheetOpen && (
				<ActionSheet title={moreLabel} actions={overflow} onClose={closeSheet} onSelect={select} />
			)}
		</Bar>
	);
};
